import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import api from "@/lib/api";
import { 
  Building2, Stethoscope, Hospital, User, Mail, Phone, MapPin, 
  FileText, Lock, ArrowRight, CheckCircle2, Clock, Heart
} from "lucide-react";

export default function PartnerRegistration() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    organizationType: "",
    organizationName: "",
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    address: "",
    npiNumber: "",
    specialty: ""
  });
  const [error, setError] = useState("");

  const organizationTypes = [
    { value: "facility", label: "Skilled Nursing / Long-Term Care Facility", icon: Building2, color: "bg-blue-500" },
    { value: "hospital", label: "Hospital / Health System", icon: Hospital, color: "bg-purple-500" },
    { value: "physician", label: "Physician / Medical Practice", icon: Stethoscope, color: "bg-emerald-500" }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validation
    if (!formData.organizationType) {
      setError("Please select an organization type");
      return;
    }
    if (!formData.organizationName) {
      setError("Organization name is required");
      return;
    }
    if (!formData.name || !formData.email || !formData.password) {
      setError("Please fill in all required fields");
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    setLoading(true);
    try {
      await api.post("/partners/register", {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        organization_name: formData.organizationName,
        organization_type: formData.organizationType,
        npi_number: formData.npiNumber || null,
        phone: formData.phone,
        address: formData.address || null,
        specialty: formData.specialty || null
      });

      setSuccess(true);
      toast.success("Registration submitted successfully!");
    } catch (err) {
      setError(err.response?.data?.detail || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-primary/5 to-background flex items-center justify-center p-6">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="font-serif text-2xl mb-2">Registration Submitted!</h2>
            <p className="text-muted-foreground mb-6">
              Your partner registration has been submitted for review. You will be notified once your account is approved.
            </p>
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 mb-6">
              <div className="flex items-center gap-2 text-amber-700 mb-2">
                <Clock className="w-5 h-5" />
                <span className="font-medium">Pending Approval</span>
              </div>
              <p className="text-sm text-amber-600">
                Our team will review your application within 1-2 business days.
              </p>
            </div>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => navigate("/")}>
                Return Home
              </Button>
              <Button onClick={() => navigate("/login")}>
                Sign In
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/5 to-background py-12 px-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-primary hover:text-primary/80 mb-4">
            <Heart className="w-6 h-6" />
            <span className="font-serif text-xl">Goodwin Cares</span>
          </Link>
          <h1 className="font-serif text-3xl text-foreground mb-2">Partner Registration</h1>
          <p className="text-muted-foreground">
            Join our network of healthcare partners to collaborate on patient care
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Create Your Partner Account</CardTitle>
            <CardDescription>
              Facilities, hospitals, and physicians can register to access patient information and collaborate with our hospice team.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Organization Type Selection */}
              <div className="space-y-3">
                <Label className="text-base font-medium">Organization Type *</Label>
                <div className="grid gap-3">
                  {organizationTypes.map((type) => {
                    const Icon = type.icon;
                    const isSelected = formData.organizationType === type.value;
                    return (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => handleChange("organizationType", type.value)}
                        className={`flex items-center gap-4 p-4 rounded-lg border-2 transition-all text-left ${
                          isSelected 
                            ? 'border-primary bg-primary/5' 
                            : 'border-muted hover:border-muted-foreground/50'
                        }`}
                      >
                        <div className={`p-3 rounded-lg ${type.color} text-white`}>
                          <Icon className="w-6 h-6" />
                        </div>
                        <div>
                          <p className="font-medium">{type.label}</p>
                          <p className="text-sm text-muted-foreground">
                            {type.value === "facility" && "SNF, ALF, Memory Care, Rehab Centers"}
                            {type.value === "hospital" && "Acute Care, Community Hospitals"}
                            {type.value === "physician" && "Primary Care, Specialists, Group Practices"}
                          </p>
                        </div>
                        {isSelected && (
                          <CheckCircle2 className="w-5 h-5 text-primary ml-auto" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Organization Details */}
              {formData.organizationType && (
                <>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="organizationName">
                        <Building2 className="w-4 h-4 inline mr-1" />
                        Organization Name *
                      </Label>
                      <Input
                        id="organizationName"
                        value={formData.organizationName}
                        onChange={(e) => handleChange("organizationName", e.target.value)}
                        placeholder="e.g., Sunrise Senior Living"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="npiNumber">
                        <FileText className="w-4 h-4 inline mr-1" />
                        NPI Number
                      </Label>
                      <Input
                        id="npiNumber"
                        value={formData.npiNumber}
                        onChange={(e) => handleChange("npiNumber", e.target.value)}
                        placeholder="10-digit NPI"
                      />
                    </div>
                  </div>

                  {formData.organizationType === "physician" && (
                    <div className="space-y-2">
                      <Label htmlFor="specialty">
                        <Stethoscope className="w-4 h-4 inline mr-1" />
                        Specialty
                      </Label>
                      <Select value={formData.specialty} onValueChange={(v) => handleChange("specialty", v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select specialty..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="primary_care">Primary Care / Family Medicine</SelectItem>
                          <SelectItem value="internal_medicine">Internal Medicine</SelectItem>
                          <SelectItem value="geriatrics">Geriatrics</SelectItem>
                          <SelectItem value="oncology">Oncology</SelectItem>
                          <SelectItem value="cardiology">Cardiology</SelectItem>
                          <SelectItem value="pulmonology">Pulmonology</SelectItem>
                          <SelectItem value="nephrology">Nephrology</SelectItem>
                          <SelectItem value="neurology">Neurology</SelectItem>
                          <SelectItem value="palliative">Palliative Care</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="address">
                      <MapPin className="w-4 h-4 inline mr-1" />
                      Address
                    </Label>
                    <Textarea
                      id="address"
                      value={formData.address}
                      onChange={(e) => handleChange("address", e.target.value)}
                      placeholder="Street address, city, state, ZIP"
                      rows={2}
                    />
                  </div>

                  <hr className="my-6" />
                  <h3 className="font-medium text-lg mb-4">Contact Information</h3>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">
                        <User className="w-4 h-4 inline mr-1" />
                        Contact Person Name *
                      </Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => handleChange("name", e.target.value)}
                        placeholder="Your full name"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">
                        <Phone className="w-4 h-4 inline mr-1" />
                        Phone Number *
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleChange("phone", e.target.value)}
                        placeholder="(703) 555-0100"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">
                      <Mail className="w-4 h-4 inline mr-1" />
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      placeholder="you@organization.com"
                      required
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="password">
                        <Lock className="w-4 h-4 inline mr-1" />
                        Password *
                      </Label>
                      <Input
                        id="password"
                        type="password"
                        value={formData.password}
                        onChange={(e) => handleChange("password", e.target.value)}
                        placeholder="Min. 8 characters"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">
                        <Lock className="w-4 h-4 inline mr-1" />
                        Confirm Password *
                      </Label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        value={formData.confirmPassword}
                        onChange={(e) => handleChange("confirmPassword", e.target.value)}
                        placeholder="Confirm password"
                        required
                      />
                    </div>
                  </div>
                </>
              )}

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button 
                type="submit" 
                className="w-full gap-2" 
                size="lg"
                disabled={loading || !formData.organizationType}
              >
                {loading ? "Submitting..." : "Submit Registration"}
                <ArrowRight className="w-4 h-4" />
              </Button>

              <p className="text-center text-sm text-muted-foreground">
                Already have an account?{" "}
                <Link to="/login" className="text-primary hover:underline">
                  Sign in here
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>

        {/* Contact Info */}
        <div className="mt-8 text-center text-muted-foreground">
          <p>Questions about partnering with Goodwin Cares?</p>
          <p className="mt-1">
            Call <a href="tel:703-578-7108" className="text-primary">703-578-7108</a> or email{" "}
            <a href="mailto:hospice@goodwinliving.org" className="text-primary">hospice@goodwinliving.org</a>
          </p>
        </div>
      </div>
    </div>
  );
}
